import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  InputBase,
  Button,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { summaryCardTheme as t } from "./styles";

const formatCurrency = (value) =>
  typeof value === "number" && value > 0
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    : "—";

const formatServiceName = (name) =>
  name
    .split(/[-_]/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(" ");

const COLUMNS = [
  { id: "service", label: "AWS service", desc: "Service or product (e.g. IAM, S3)", sortable: true, width: "14%" },
  { id: "arns_total", label: "ARNs", desc: "Total ARNs scanned for this service", sortable: true, align: "right", width: "10%" },
  { id: "security_failed_checks_total", label: "CSPM Findings", desc: "Total CSPM (security) findings for this service", sortable: true, align: "right", width: "11%" },
  { id: "compliance_failed_controls_total", label: "Control ID Findings", desc: "Total compliance control ID findings", sortable: true, align: "right", width: "12%" },
  { id: "compliance_frameworks_failed_count", label: "Framework findings", desc: "Compliance frameworks with at least one finding", sortable: true, align: "right", width: "11%" },
  { id: "arns_cost", label: "Cloud Waste", desc: "ARNs from Cost Insight data", sortable: true, align: "right", width: "10%" },
  { id: "cost_unblended_mtd_total", label: "Cost (MTD)", desc: "Unblended cost month-to-date for this service", sortable: true, align: "right", width: "11%" },
  { id: "arns_all_three", label: "Security+Compliance+Cost Findings", desc: "ARNs common to CSPM, Compliance and Cost Insight findings", sortable: true, align: "right", width: "12%" },
];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

function SummaryServicesTable({ services }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [orderBy, setOrderBy] = useState("arns_total");
  const [order, setOrder] = useState("desc");

  const filteredAndSorted = useMemo(() => {
    let list = Array.isArray(services) ? [...services] : [];
    const q = (search || "").trim().toLowerCase();
    if (q) {
      list = list.filter((row) =>
        (row.service || "").toLowerCase().includes(q)
      );
    }
    const col = COLUMNS.find((c) => c.id === orderBy);
    if (!col || !col.sortable) return list;
    list.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      const an = typeof av === "number" ? av : (av || "").toString().toLowerCase();
      const bn = typeof bv === "number" ? bv : (bv || "").toString().toLowerCase();
      if (typeof an === "number" && typeof bn === "number") {
        return order === "asc" ? an - bn : bn - an;
      }
      const aa = String(an);
      const bb = String(bn);
      return order === "asc" ? (aa < bb ? -1 : 1) : (bb < aa ? -1 : 1);
    });
    return list;
  }, [services, search, orderBy, order]);

  const pagedRows = useMemo(
    () =>
      filteredAndSorted.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [filteredAndSorted, page, rowsPerPage]
  );

  const handleSort = (id) => {
    if (orderBy === id) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setOrderBy(id);
      setOrder("desc");
    }
    setPage(0);
  };

  const total = filteredAndSorted.length;
  const from = total === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min((page + 1) * rowsPerPage, total);

  const cellStyle = (col, row) => {
    const val = row[col.id];
    const num = typeof val === "number" ? val : 0;
    if (col.id === "security_failed_checks_total" || col.id === "arns_all_three") {
      return { color: num > 0 ? t.colors.issue : t.colors.textMuted };
    }
    if (col.id === "compliance_failed_controls_total" || col.id === "compliance_frameworks_failed_count") {
      return { color: num > 0 ? t.colors.warning : t.colors.textMuted };
    }
    return { color: t.colors.text };
  };

  const renderCell = (col, row) => {
    const val = row[col.id];
    if (col.id === "service") return formatServiceName(val);
    if (col.id === "cost_unblended_mtd_total") return formatCurrency(val);
    if (col.id === "arns_cost") return val ?? 0;
    return val ?? 0;
  };

  return (
    <Box
      sx={{
        backgroundColor: t.colors.card,
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.radius.lg,
        overflow: "hidden",
        boxShadow: t.shadow,
        fontFamily: t.fontFamily,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${t.colors.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.875rem", fontFamily: t.fontFamily }}>
            By service
          </Typography>
          <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", mt: 0.25, fontFamily: t.fontFamily }}>
            Security, compliance, and cost per AWS service — sort and search below
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: `1px solid ${t.colors.border}`,
            borderRadius: t.radius.sm,
            pl: 1,
            pr: 1.5,
            py: 0.5,
            backgroundColor: t.colors.background,
            minWidth: 200,
          }}
        >
          <SearchIcon sx={{ color: t.colors.textMuted, fontSize: 18, mr: 0.5 }} />
          <InputBase
            placeholder="Search service..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ fontSize: "0.8125rem", fontFamily: t.fontFamily, "& input": { py: 0.5 } }}
          />
        </Box>
      </Box>
      <TableContainer sx={{ maxHeight: 320 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align || "left"}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    color: t.colors.textMuted,
                    backgroundColor: t.colors.borderLight,
                    borderBottom: `1px solid ${t.colors.border}`,
                    py: 1.25,
                    px: 1.5,
                    fontFamily: t.fontFamily,
                    width: col.width,
                    cursor: col.sortable ? "pointer" : "default",
                    userSelect: "none",
                  }}
                  onClick={() => col.sortable && handleSort(col.id)}
                >
                  <Tooltip title={col.desc} arrow>
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                      {col.label}
                      {col.sortable && orderBy === col.id && (
                        order === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length}
                  align="center"
                  sx={{ py: 4, color: t.colors.textMuted, fontSize: "0.875rem", fontFamily: t.fontFamily }}
                >
                  {search ? "No services match your search" : "No service data"}
                </TableCell>
              </TableRow>
            ) : (
              pagedRows.map((row, index) => (
                <TableRow
                  key={row.service ?? index}
                  sx={{
                    "& td": {
                      borderBottom: `1px solid ${t.colors.borderLight}`,
                      py: 1.25,
                      px: 1.5,
                      fontSize: "0.8125rem",
                      fontFamily: t.fontFamily,
                    },
                    "&:hover": { backgroundColor: t.colors.background },
                  }}
                >
                  {COLUMNS.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align || "left"}
                      sx={{ ...cellStyle(col, row), fontFamily: t.fontFamily }}
                    >
                      {renderCell(col, row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Custom footer — BillingManagement-style */}
      {filteredAndSorted.length > 0 && (
        <Box
          sx={{
            p: 1.5,
            borderTop: `1px solid ${t.colors.border}`,
            backgroundColor: t.colors.card,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            borderRadius: "0 0 12px 12px",
          }}
        >
          <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.8125rem", fontWeight: 400, fontFamily: t.fontFamily }}>
            Showing {from}–{to} of {total} services
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", fontFamily: t.fontFamily }}>
              Rows per page
            </Typography>
            <FormControl size="small" sx={{ minWidth: 56 }}>
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                sx={{
                  fontSize: "0.8125rem",
                  height: 32,
                  fontFamily: t.fontFamily,
                  color: t.colors.text,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.border },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.borderLight },
                }}
              >
                {ROWS_PER_PAGE_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontFamily: t.fontFamily, fontSize: "0.8125rem" }}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              sx={{
                textTransform: "none",
                fontSize: "0.8125rem",
                borderColor: t.colors.border,
                color: t.colors.textMuted,
                minWidth: "auto",
                px: 1.5,
                "&:hover": { borderColor: t.colors.borderLight, backgroundColor: t.colors.background },
                "&.Mui-disabled": { borderColor: t.colors.borderLight, color: t.colors.textMutedLight },
              }}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={to >= total}
              onClick={() => setPage((p) => p + 1)}
              sx={{
                textTransform: "none",
                fontSize: "0.8125rem",
                borderColor: t.colors.border,
                color: t.colors.textMuted,
                minWidth: "auto",
                px: 1.5,
                "&:hover": { borderColor: t.colors.borderLight, backgroundColor: t.colors.background },
                "&.Mui-disabled": { borderColor: t.colors.borderLight, color: t.colors.textMutedLight },
              }}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default SummaryServicesTable;
